import { CartLinesUpdateEvent } from '@shopify/events';

let pendingMutations = 0;
const elements = () => document.querySelectorAll('nc-offer-form,nc-bundle-builder,nc-cart-upgrade');
const root = () => window.Shopify?.routes?.root || '/';
const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:currency || 'USD'}).format(cents / 100);
const refreshControls = () => elements().forEach(element => element.sync?.());

document.addEventListener(CartLinesUpdateEvent.eventName, event => {
  if (!event.promise) return;
  pendingMutations++;
  refreshControls();
  Promise.resolve(event.promise).catch(() => {}).finally(() => {
    pendingMutations = Math.max(0, pendingMutations - 1);
    refreshControls();
  });
});

async function readResponse(response) {
  const type = response.headers.get('content-type') || '';
  if (!/\b(json|javascript)\b/i.test(type)) {
    throw new Error(response.status === 429 ? 'Shopify could not verify this request. Please open your bag and try again.' : 'Your bag could not be updated. Please try again.');
  }
  const data = await response.json();
  if (!response.ok || data.status || data.errors) {
    const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'This quantity is unavailable. Please review your selection.';
    throw new Error(message);
  }
  return data;
}

function focusDrawerOffers(drawer, behavior = 'instant') {
  const heading = drawer.querySelector('#nc-drawer-offers-title');
  const scroller = heading?.closest('.cart-drawer__content');
  if (!heading || !scroller) return;
  const top = scroller.scrollTop + heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 12;
  scroller.scrollTo({top:Math.max(0,top),behavior});
  heading.focus({preventScroll:true});
}

function preserveDrawerFocus(host) {
  const drawer = host.closest('#cart-drawer');
  const origin = document.activeElement;
  if (!drawer || !host.contains(origin)) return () => {};
  let moved = false;
  const onFocus = event => {
    if (event.target !== document.body && event.target !== origin && !host.contains(event.target)) moved = true;
  };
  document.addEventListener('focusin',onFocus);
  return success => {
    requestAnimationFrame(() => {
      document.removeEventListener('focusin',onFocus);
      if (!success || moved || !drawer.isConnected || !drawer.querySelector('dialog[open]')) return;
      if (document.activeElement === document.body) focusDrawerOffers(drawer);
    });
  };
}

async function updateCart(host, payload, action = 'add') {
  if (pendingMutations || host.hasAttribute('data-committed')) return false;
  const finishFocus = preserveDrawerFocus(host);
  const deferred = CartLinesUpdateEvent.createPromise();
  deferred.promise.catch(() => {});
  const status = host.querySelector('[data-status]');
  status.textContent = action === 'add' ? 'Adding your selection...' : 'Updating your bag...';
  host.setAttribute('aria-busy','true');
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0,5);
  const lines = action === 'add' ? payload.items.map(item => ({merchandiseId:String(item.id),quantity:item.quantity})) : [{id:payload.id,quantity:payload.quantity}];
  host.dispatchEvent(new CartLinesUpdateEvent({action,context:action === 'add' && !document.querySelector('main[data-template="cart"]') ? 'product' : 'cart',lines,promise:deferred.promise}));
  let committed = false;
  try {
    const response = await fetch(root() + (action === 'add' ? 'cart/add.js' : 'cart/change.js'), {
      method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
      body:JSON.stringify({...payload,sections,sections_url:location.pathname + location.search})
    });
    const changed = await readResponse(response);
    committed = true;
    host.setAttribute('data-committed','');
    const cart = action === 'add' ? await readResponse(await fetch(root()+'cart.js',{headers:{Accept:'application/json'}})) : changed;
    deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,itemCount:cart.item_count,sections:changed.sections,source:'nc-commerce',didError:false}});
    status.textContent = action === 'add' ? 'Added to your bag.' : 'Your bag has been updated.';
    host.querySelector('.ncc-review-cart')?.removeAttribute('hidden');
    host.removeAttribute('data-committed');
    if (!document.querySelector('cart-items-component')) location.assign(root()+'cart');
    return true;
  } catch (error) {
    deferred.reject(error);
    if (committed) {
      // The mutation succeeded. Navigate to the authoritative cart instead of inviting a duplicate add.
      status.textContent = 'Your items were added. Opening your bag...';
      location.assign(root()+'cart');
    } else {
      status.textContent = error.message || 'Your bag could not be updated. Please try again.';
      host.querySelector('.ncc-review-cart')?.removeAttribute('hidden');
    }
    return false;
  } finally {
    host.removeAttribute('aria-busy');
    host.sync?.();
    finishFocus(committed);
  }
}

class NcOfferForm extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();this.abort = new AbortController();
    const {signal}=this.abort;
    this.addEventListener('change',()=>this.sync(),{signal});
    // Shopify morphs child forms during cart refreshes; delegate on the stable host.
    this.addEventListener('submit',event=>{
      const form=event.target;
      if (!(form instanceof HTMLFormElement) || form.closest('nc-offer-form') !== this) return;
      event.preventDefault();
      if (!form.reportValidity() || pendingMutations) return;
      const id=Number(form.elements.id.value),quantity=Number(form.elements.quantity.value);
      if (!Number.isSafeInteger(id) || !Number.isSafeInteger(quantity) || quantity<1) return;
      updateCart(this,{items:[{id,quantity}]});
    },{signal});
    this.sync();
  }
  disconnectedCallback(){this.abort?.abort();}
  sync(){
    const select=this.querySelector('select[name=id]'),option=select?.selectedOptions[0];
    if(option){
      this.querySelector('[data-offer-price]').textContent=money(Number(option.dataset.price),this.dataset.currency);
      this.querySelector('input[name=quantity]').value=option.dataset.quantity || '1';
    }
    const button=this.querySelector('button[type=submit]');
    if(!button)return;
    if(this.available===undefined)this.available=!button.disabled;
    button.disabled=pendingMutations>0||this.hasAttribute('data-committed')||(option?option.disabled:!this.available);
  }
}

class NcBundleBuilder extends HTMLElement {
  connectedCallback(){
    this.abort?.abort();this.abort=new AbortController();this.revision=0;
    const {signal}=this.abort;
    this.addEventListener('change',event=>{
      if(event.target.matches('[data-bundle-variant]')){
        const row=event.target.closest('[data-bundle-item]'),option=event.target.selectedOptions[0],input=row.querySelector('[data-bundle-quantity]');
        input.min=option.dataset.min||'1';input.step=option.dataset.step||'1';
        if(option.dataset.max)input.max=option.dataset.max;else input.removeAttribute('max');
        if(!input.checkValidity())input.value=input.min;
      }
      this.sync();
    },{signal});
    this.addEventListener('input',()=>this.sync(),{signal});
    this.addEventListener('click',event=>{
      const step=event.target.closest('[data-bundle-step]');
      if(!step||pendingMutations)return;
      const row=step.closest('[data-bundle-item]'),input=row.querySelector('[data-bundle-quantity]');
      if(step.dataset.bundleStep==='1')input.stepUp();else input.stepDown();
      input.dispatchEvent(new Event('input',{bubbles:true}));
    },{signal});
    this.dock=this.querySelector('[data-bundle-dock]');
    this.summary=this.querySelector('.ncc-bundle-summary');
    this.observer?.disconnect();
    this.observer=new IntersectionObserver(entries=>{for(const entry of entries){if(entry.target===this)this.inView=entry.isIntersecting;else this.summaryInView=entry.isIntersecting;}this.updateDock();});
    this.observer.observe(this);if(this.summary)this.observer.observe(this.summary);
    this.dock?.addEventListener('click',event=>{event.preventDefault();this.summary?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});this.summary?.focus({preventScroll:true});},{signal});
    this.querySelector('[data-bundle-submit]')?.addEventListener('click',()=>{
      const state=this.selection();
      if(!state.valid||pendingMutations)return;
      updateCart(this,{items:state.items.map(item=>({id:item.id,quantity:item.quantity}))});
    },{signal});
    document.addEventListener('shopify:product:select',event=>{
      if(!event.promise)return;
      const revision=++this.revision;
      event.promise.then(({detail})=>{
        if(!this.isConnected||revision!==this.revision)return;
        const row=this.querySelector('[data-primary]'),select=row?.querySelector('[data-bundle-variant]');
        if(select&&[...select.options].some(option=>option.value===String(detail?.resource?.id))){
          select.value=String(detail.resource.id);select.dispatchEvent(new Event('change',{bubbles:true}));
        }
      }).catch(()=>{});
    },{signal});
    this.sync();
  }
  disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();}
  updateDock(){if(this.dock)this.dock.hidden=!this.inView||this.summaryInView||!this.selectedCount;}
  selection(){
    const items=[];let invalid=false;
    for(const row of this.querySelectorAll('[data-bundle-item]')){
      const checked=row.querySelector('[data-bundle-check]').checked;
      row.classList.toggle('is-selected',checked);
      const option=row.querySelector('[data-bundle-variant]').selectedOptions[0],input=row.querySelector('[data-bundle-quantity]');
      const price=Number(option?.dataset.price||0);
      row.querySelector('[data-bundle-price]').textContent=money(price,this.dataset.currency);
      const decrement=row.querySelector('[data-bundle-step="-1"]'),increment=row.querySelector('[data-bundle-step="1"]');
      if(decrement)decrement.disabled=pendingMutations>0||Number(input.value)<=Number(input.min)||option?.disabled;
      if(increment)increment.disabled=pendingMutations>0||(input.max!==''&&Number(input.value)>=Number(input.max))||option?.disabled;
      if(!checked)continue;
      const quantity=Number(input.value),id=Number(option?.value);
      const valid=option?.dataset.available==='true'&&!option.disabled&&input.checkValidity()&&Number.isSafeInteger(quantity)&&quantity>0&&Number.isSafeInteger(id)&&Number.isSafeInteger(price)&&price>=0;
      if(!valid){invalid=true;continue;}
      items.push({id,quantity,price,eligible:row.dataset.offerEligible==='true',title:row.querySelector('h3').textContent.trim()});
    }
    const subtotal=items.reduce((sum,item)=>sum+item.price*item.quantity,0);
    const eligibleCount=items.filter(item=>item.eligible).reduce((sum,item)=>sum+item.quantity,0);
    const discount=this.dataset.offersActive==='true'?(eligibleCount>=3?15:eligibleCount>=2?10:0):0;
    const savings=items.reduce((sum,item)=>sum+(item.eligible?Math.floor(item.price*discount/100)*item.quantity:0),0);
    const total=subtotal-savings;
    return {items,total,subtotal,savings,discount,valid:!invalid&&Number.isSafeInteger(total)&&items.length>=Number(this.dataset.minProducts||2),invalid};
  }
  sync(){
    const state=this.selection(),list=this.querySelector('[data-bundle-list]');list.replaceChildren();
    this.selectedCount=state.items.reduce((n,item)=>n+item.quantity,0);
    const dockCount=this.querySelector('[data-bundle-dock-count]'),dockTotal=this.querySelector('[data-bundle-dock-total]');
    if(dockCount)dockCount.textContent=this.selectedCount+' '+(this.selectedCount===1?'item':'items')+' selected';
    if(dockTotal)dockTotal.textContent=state.invalid?'Check quantities':money(state.total,this.dataset.currency)+' estimated total';
    this.updateDock();
    if(!state.items.length){const li=document.createElement('li');li.textContent='Choose your first product';list.append(li);}
    for(const item of state.items){const li=document.createElement('li');const title=document.createElement('span'),amount=document.createElement('b');title.textContent=item.quantity+' '+String.fromCharCode(215)+' '+item.title;amount.textContent=money(item.price*item.quantity,this.dataset.currency);li.append(title,amount);list.append(li);}
    this.querySelector('[data-bundle-total]').textContent=money(state.total,this.dataset.currency);
    const subtotal=this.querySelector('[data-bundle-subtotal]'),saving=this.querySelector('[data-bundle-saving]');
    if(subtotal){subtotal.hidden=!state.savings;subtotal.textContent=money(state.subtotal,this.dataset.currency);}
    if(saving){saving.hidden=!state.savings;saving.textContent='Save '+money(state.savings,this.dataset.currency)+' with the '+state.discount+'% offer';}
    const button=this.querySelector('[data-bundle-submit]');button.disabled=!state.valid||pendingMutations>0||this.hasAttribute('data-committed');
    button.textContent=state.valid?'Add '+state.items.reduce((n,item)=>n+item.quantity,0)+' items to bag':'Choose 2+ products';
    const status=this.querySelector('[data-status]');
    if(!this.hasAttribute('aria-busy') && !this.hasAttribute('data-committed')){
      if(state.invalid)status.textContent='Review the quantities and available options in your selection.';
      else if(!state.valid)status.textContent='Choose at least two different products.';
      else if(status.textContent.startsWith('Choose ')||status.textContent.startsWith('Review the quantities'))status.textContent='Your selection is ready.';
    }
  }
}

class NcCartUpgrade extends HTMLElement {
  connectedCallback(){this.abort?.abort();this.abort=new AbortController();this.addEventListener('click',event=>{if(event.target instanceof Element && event.target.closest('[data-upgrade-submit]') && !pendingMutations)updateCart(this,{id:this.dataset.lineKey,quantity:Number(this.dataset.quantity)},'update');},{signal:this.abort.signal});this.sync();}
  disconnectedCallback(){this.abort?.abort();}
  sync(){const button=this.querySelector('button');if(button)button.disabled=pendingMutations>0||this.hasAttribute('data-committed');}
}
for(const [name,definition] of [['nc-offer-form',NcOfferForm],['nc-bundle-builder',NcBundleBuilder],['nc-cart-upgrade',NcCartUpgrade]])if(!customElements.get(name))customElements.define(name,definition);

// Keep optional drawer additions reachable without moving the checkout action.
document.addEventListener('click', event => {
  if (!(event.target instanceof Element) || !event.target.closest('[data-drawer-offers]')) return;
  const drawer = event.target.closest('#cart-drawer');
  if (drawer) focusDrawerOffers(drawer,matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth');
});
